import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription 
} from './ui/dialog';
import { 
  Tag, 
  Plus, 
  Edit2, 
  Trash2, 
  X,
  Loader2,
  Palette
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cores predefinidas para tags
const CORES_TAGS = [
  '#ef4444', // red
  '#f97316', // orange
  '#f59e0b', // amber
  '#eab308', // yellow
  '#84cc16', // lime
  '#22c55e', // green
  '#14b8a6', // teal
  '#06b6d4', // cyan
  '#0ea5e9', // sky
  '#3b82f6', // blue
  '#6366f1', // indigo
  '#8b5cf6', // violet
  '#a855f7', // purple
  '#d946ef', // fuchsia
  '#ec4899', // pink
  '#f43f5e', // rose
];

export default function GestaoTags({ open, onClose, onTagsChange }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [formData, setFormData] = useState({ nome: '', cor: '#6366f1', descricao: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      loadTags();
    }
  }, [open]);

  const loadTags = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/tags`);
      setTags(response.data);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
      toast.error('Erro ao carregar tags');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim()) {
      toast.error('O nome da tag é obrigatório');
      return;
    }

    setSaving(true);
    try {
      if (editingTag) {
        await axios.put(`${API}/tags/${editingTag.id}`, formData);
        toast.success('Tag atualizada');
      } else {
        await axios.post(`${API}/tags`, formData);
        toast.success('Tag criada');
      }
      setShowForm(false);
      setEditingTag(null);
      setFormData({ nome: '', cor: '#6366f1', descricao: '' });
      loadTags();
      if (onTagsChange) onTagsChange();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao guardar tag'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (tag) => {
    setFormData({
      nome: tag.nome,
      cor: tag.cor,
      descricao: tag.descricao || ''
    });
    setEditingTag(tag);
    setShowForm(true);
  };

  const handleDelete = async (tag) => {
    if (!window.confirm(`Tem a certeza que deseja eliminar a tag "${tag.nome}"?`)) return;
    
    try {
      await axios.delete(`${API}/tags/${tag.id}`);
      toast.success('Tag eliminada');
      loadTags();
      if (onTagsChange) onTagsChange();
    } catch (error) {
      toast.error('Erro ao eliminar tag');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingTag(null);
    setFormData({ nome: '', cor: '#6366f1', descricao: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-heading">
            <Tag className="h-5 w-5 text-primary" />
            Gestão de Tags
          </DialogTitle>
          <DialogDescription>
            Crie e gerir tags para categorizar operadores
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Botão Nova Tag */}
          {!showForm && (
            <Button
              onClick={() => setShowForm(true)}
              className="w-full"
              data-testid="btn-nova-tag"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Tag
            </Button>
          )}

          {/* Formulário */}
          {showForm && (
            <Card className="p-4 border-2 border-primary/30 bg-primary/5">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                {editingTag ? <Edit2 className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {editingTag ? 'Editar Tag' : 'Nova Tag'}
              </h4>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Nome *
                  </label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Ex: Especialista Costura"
                    data-testid="input-tag-nome"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1 flex items-center gap-1">
                    <Palette className="h-3 w-3" /> Cor
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {CORES_TAGS.map((cor) => (
                      <button
                        key={cor}
                        type="button"
                        onClick={() => setFormData({ ...formData, cor })}
                        className={`w-7 h-7 rounded-full transition-transform ${
                          formData.cor === cor ? 'ring-2 ring-offset-2 ring-stone-400 scale-110' : 'hover:scale-110'
                        }`}
                        style={{ backgroundColor: cor }}
                      />
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Descrição (opcional)
                  </label>
                  <Input
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    placeholder="Descrição da tag"
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetForm}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1"
                    data-testid="btn-guardar-tag"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    {editingTag ? 'Atualizar' : 'Criar'}
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {/* Lista de Tags */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : tags.length === 0 ? (
            <div className="text-center py-8">
              <Tag className="h-12 w-12 mx-auto text-stone-300 mb-3" />
              <p className="text-stone-500">Nenhuma tag criada</p>
              <p className="text-sm text-stone-400">Crie tags para organizar os operadores</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-stone-500 font-medium">{tags.length} tag(s)</p>
              {tags.map((tag) => (
                <Card 
                  key={tag.id} 
                  className="p-3 flex items-center justify-between hover:shadow-sm transition-shadow"
                  data-testid={`tag-card-${tag.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.cor }}
                    />
                    <div>
                      <span className="font-medium text-stone-900">{tag.nome}</span>
                      {tag.descricao && (
                        <p className="text-xs text-stone-500">{tag.descricao}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleEdit(tag)}
                    >
                      <Edit2 className="h-4 w-4 text-stone-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(tag)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Componente para exibir tags de um operador
export function OperadorTags({ tags, allTags, size = 'sm' }) {
  if (!tags || tags.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((tagId) => {
        const tag = allTags?.find(t => t.id === tagId);
        if (!tag) return null;
        return (
          <span
            key={tagId}
            className={`inline-flex items-center gap-1 rounded-full text-white ${
              size === 'xs' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'
            }`}
            style={{ backgroundColor: tag.cor }}
          >
            {tag.nome}
          </span>
        );
      })}
    </div>
  );
}

// Componente para selecionar tags
export function TagSelector({ selectedTags = [], allTags = [], onChange, maxTags = 10 }) {
  const handleToggle = (tagId) => {
    if (selectedTags.includes(tagId)) {
      onChange(selectedTags.filter(id => id !== tagId));
    } else if (selectedTags.length < maxTags) {
      onChange([...selectedTags, tagId]);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {allTags.map((tag) => {
        const isSelected = selectedTags.includes(tag.id);
        return (
          <button
            key={tag.id}
            type="button"
            onClick={() => handleToggle(tag.id)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
              isSelected 
                ? 'text-white ring-2 ring-offset-1' 
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
            style={isSelected ? { backgroundColor: tag.cor, ringColor: tag.cor } : {}}
            data-testid={`tag-select-${tag.id}`}
          >
            {!isSelected && (
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.cor }} />
            )}
            {tag.nome}
            {isSelected && <X className="h-3 w-3" />}
          </button>
        );
      })}
      {allTags.length === 0 && (
        <span className="text-xs text-stone-400">Nenhuma tag disponível</span>
      )}
    </div>
  );
}
